import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { ImpresorasService } from './features/configuration/services/impresoras.service';
import { ChatAgent } from './features/main/components/chat-agent/chat-agent';
import { SucursalSelector } from './features/main/components/sucursal-selector/sucursal-selector';
import { ToastMessageContainer } from './shared/components/toast-message-container/toast-message-container';
import { AppFeatureEnabled } from './shared/directives/appAuthFeature.directive';
import { AuthGroupsDirective } from './shared/directives/appAuthGroups.directive';
import { AuthIsLogiFlowDirective } from './shared/directives/appAuthIsLogiFlow.directive';
import { GroupNames } from './shared/models/group-names';
import { ApplicationSessionService } from './shared/services/application-session.service';
import { AuthService } from './shared/services/auth.service';
import { ModalService } from './shared/services/modal.service';
import { NetworkService } from './shared/services/network-service.service';
import { ToastMessageService } from './shared/services/toast-message.service';

interface MenuSearchResult {
  label: string;
  title: string;
  url: string;
  href: string;
}

interface PwaBeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, FormsModule, ToastMessageContainer, AuthGroupsDirective, RouterLinkActive, AuthIsLogiFlowDirective, ChatAgent, AppFeatureEnabled],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {

  readonly networkService = inject(NetworkService);

  constructor(public auth: AuthService, private appSession: ApplicationSessionService, private route: ActivatedRoute, private modal: ModalService) {
  }
  GroupNames = GroupNames;
  readonly toast = inject(ToastMessageService);
  readonly impresoraService = inject(ImpresorasService);
  @ViewChild('navbarRoot') navbarRoot?: ElementRef<HTMLElement>;
  readonly menuSearchQuery = signal('');
  readonly menuSearchResults = signal<MenuSearchResult[]>([]);
  readonly menuSearchActiveIndex = signal(-1);
  @ViewChild('menuSearch') menuSearchInput?: ElementRef<HTMLInputElement>;
  errorMessage = signal<string>("");
  readonly isOffline = computed<boolean>(() => this.networkService.isOffline());
  private deferredInstallPrompt: PwaBeforeInstallPromptEvent | null = null;
  readonly modals = inject(ModalService);
  private readonly router = inject(Router);

  private readonly onBeforeInstallPrompt = async (event: Event) => {
    const installEvent = event as PwaBeforeInstallPromptEvent;

    event.preventDefault();
    this.deferredInstallPrompt = installEvent;

    if (!environment.pwa)
      return;

    if (localStorage.getItem('pwa-install-denied') == 'true')
      return;


    const shouldInstall = await this.modals.confirm('Instalar aplicación', '¿Desea instalar la aplicación para usarla como app?');
    if (!shouldInstall) {
      {
        localStorage.setItem('pwa-install-denied', 'true');
        return;
      }
    }

    await this.promptPwaInstall();
  };
  private readonly onAppInstalled = () => {
    this.deferredInstallPrompt = null;
  };

  private readonly onGlobalKeyDown = (event: KeyboardEvent) => {
    if (!event.ctrlKey || event.key.toLowerCase() !== 'b') {
      return;
    }

    event.preventDefault();
    this.focusMenuSearch();
  };


  async configurePrinters() {
    await this.impresoraService.configureImpresoras();
  }

  focusMenuSearch(): void {
    const input = this.menuSearchInput?.nativeElement;
    if (!input) {
      return;
    }

    input.focus();
    input.select();
  }

  async changeSucursal() {
    let result = await this.modal.open(SucursalSelector).result;
    if (result) {
      await this.auth.setSucursal(result);
      window.location.reload();
    }
  }

  normalizeString(input: string): string {
    return input
      .normalize('NFD') // Splits accents away from their base letters
      .replace(/[\u0300-\u036f]/g, '') // Removes the accent marks
      .toLowerCase();
  }

  updateMenuSearch(menuSearch: string): void {
    this.menuSearchQuery.set(menuSearch);

    const navbar = this.navbarRoot?.nativeElement;
    if (!navbar) {
      this.menuSearchResults.set([]);
      return;
    }

    const normalizedSearch = this.normalizeString(menuSearch.trim());

    if (!normalizedSearch) {
      this.menuSearchResults.set([]);
      this.menuSearchActiveIndex.set(-1);
      return;
    }

    const menuLinks = Array.from(navbar.querySelectorAll<HTMLAnchorElement>('a'))
      .filter((link) => !link.classList.contains('navbar-brand'))
      .filter((link) => !link.closest('.navbar-search'));
    const results = menuLinks
      .map((link) => this.mapMenuSearchResult(link))
      .filter((result): result is MenuSearchResult => result !== null)
      .filter((result) => this.normalizeString(result.label).includes(normalizedSearch) || this.normalizeString(result.title).includes(normalizedSearch))
      .slice(0, 8);

    this.menuSearchResults.set(results);
    this.menuSearchActiveIndex.set(results.length > 0 ? 0 : -1);
    this.scrollActiveMenuSearchResultIntoView();
  }

  clearMenuSearch(input: HTMLInputElement): void {
    input.value = '';
    this.menuSearchQuery.set('');
    this.menuSearchResults.set([]);
    this.menuSearchActiveIndex.set(-1);
    input.focus();
  }

  handleMenuSearchKeydown(event: KeyboardEvent, input: HTMLInputElement): void {
    if (!this.menuSearchResults().length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveMenuSearchActiveIndex(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveMenuSearchActiveIndex(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const activeResult = this.menuSearchResults()[this.menuSearchActiveIndex()];
      if (activeResult) {
        void this.selectMenuResult(activeResult, input);
      }
    }
  }

  private moveMenuSearchActiveIndex(step: number): void {
    const results = this.menuSearchResults();
    if (!results.length) {
      this.menuSearchActiveIndex.set(-1);
      return;
    }

    const currentIndex = this.menuSearchActiveIndex();
    const nextIndex = currentIndex < 0
      ? 0
      : (currentIndex + step + results.length) % results.length;

    this.menuSearchActiveIndex.set(nextIndex);
    this.scrollActiveMenuSearchResultIntoView();
  }

  async selectMenuResult(result: MenuSearchResult, input?: HTMLInputElement): Promise<void> {
    this.menuSearchQuery.set('');
    this.menuSearchResults.set([]);
    this.menuSearchActiveIndex.set(-1);

    if (input) {
      input.value = '';
    }

    await this.router.navigateByUrl(this.normalizeMenuHref(result.href));
    this.closeMobileMenu();
  }

  private scrollActiveMenuSearchResultIntoView(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.requestAnimationFrame(() => {
      const navbar = this.navbarRoot?.nativeElement;
      if (!navbar) {
        return;
      }

      const activeIndex = this.menuSearchActiveIndex();
      if (activeIndex < 0) {
        return;
      }

      const activeItem = navbar.querySelector<HTMLButtonElement>(`.navbar-search__menu [data-menu-search-index="${activeIndex}"]`);
      activeItem?.scrollIntoView({ block: 'nearest' });
    });
  }

  private mapMenuSearchResult(link: HTMLAnchorElement): MenuSearchResult | null {
    const label = this.normalizeMenuLabel(link.textContent ?? '');
    const href = link.getAttribute('href')?.trim() ?? '';
    const title = this.normalizeMenuLabel(link.getAttribute('title') ?? '');

    if (link.closest('.navbar-profile-item')) {
      if (!label) {
        return null;
      }

      return { label, title: 'Perfil', url: '/perfil', href: '/perfil' };
    }

    if (!label || !href) {
      return null;
    }

    return { label, title, url: href, href };
  }

  private normalizeMenuLabel(label: string): string {
    return label.replace(/\s+/g, ' ').trim();
  }

  private normalizeMenuHref(href: string): string {
    try {
      return new URL(href, window.location.origin).pathname + new URL(href, window.location.origin).search + new URL(href, window.location.origin).hash;
    } catch {
      return href;
    }
  }

  closeMobileMenuOnSelect(event: Event): void {
    const target = event.target as HTMLElement | null;
    const selectedOption = target?.closest('a.dropdown-item, a.nav-link:not(.dropdown-toggle)');
    if (!selectedOption) {
      return;
    }

    this.closeMobileMenu();
  }

  private closeMobileMenu(): void {
    const navCollapse = document.getElementById('navbarSupportedContent');
    if (!navCollapse?.classList.contains('show')) {
      return;
    }

    navCollapse.classList.remove('show');

    const toggler = document.getElementById('navbarToggler');
    toggler?.classList.add('collapsed');
    toggler?.setAttribute('aria-expanded', 'false');
  }

  async ngOnInit(): Promise<void> {


    if (typeof window !== 'undefined') {
      //if (environment.pwa) {
      window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
      window.addEventListener('appinstalled', this.onAppInstalled);
      window.addEventListener('keydown', this.onGlobalKeyDown, true);
      //}
    }

    if (window.location.pathname.startsWith('/error')) {
      return;
    }

    if (this.auth.isAuthenticated()) {
      this.appSession.start();
    }
  }

  private async promptPwaInstall(): Promise<void> {
    const installPrompt = this.deferredInstallPrompt;
    if (!installPrompt) {
      return;
    }

    this.deferredInstallPrompt = null;
    await installPrompt.prompt();
    await installPrompt.userChoice;
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', this.onAppInstalled);
      window.removeEventListener('keydown', this.onGlobalKeyDown, true);
    }
  }
}
